import {
  extractApiErrorMessage,
  extractApiSuccessMessage,
} from '@/utils/dataNormalization';
import {
  confirmExpenseApproveFlow,
  confirmExpenseDeleteFlow,
  closeExpenseModalState,
  notifyExpenseActionPending,
  resolveExpenseApprovalTargetState,
  openExpenseEditModalFromRow,
  openExpenseModalState,
  submitExpensePayFlow,
  submitExpenseFlow,
} from '@/composables/expenses/useExpenseFlow';
import {
  fetchExpensesFilterOptions,
  fetchExpensesSnapshot,
  getExpensesFilterOptions,
} from '@/composables/expenses/useExpensesList';
import { ensureValidPage } from '@/composables/shared/usePagination';
import {
  createInitialExpenseSummary,
} from '@/composables/sales/saleDetail/state';
import { EXPENSES_TEXT } from '@/texts/catalog/expenses';

const EXPENSES_FLOW_TEXT = EXPENSES_TEXT.flow;

const toFiniteNumber = (value, fallback = 0) => {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeExpenseSummarySection = (section, fallbackTotal = 0) => {
  if (!section || typeof section !== 'object') {
    const total = toFiniteNumber(fallbackTotal, 0);

    return {
      subtotal: total,
      transferred_taxes: 0,
      withheld_taxes: 0,
      total,
    };
  }

  const total = toFiniteNumber(section.total, toFiniteNumber(fallbackTotal, 0));

  return {
    subtotal: toFiniteNumber(section.subtotal, total),
    transferred_taxes: toFiniteNumber(
      section.transferred_taxes,
      toFiniteNumber(section.transfered_taxes, 0)
    ),
    withheld_taxes: toFiniteNumber(section.withheld_taxes, 0),
    total,
  };
};

const normalizeExpenseSummaryPayload = (payload = {}) => {
  const summary = payload && typeof payload.summary === 'object' ? payload.summary : {};

  const totalFallback = toFiniteNumber(payload.total, toFiniteNumber(payload.total_expenses, 0));
  const paidFallback = toFiniteNumber(payload.paid_total, toFiniteNumber(payload.total_paid, toFiniteNumber(payload.paid, 0)));
  const pendingFallback = toFiniteNumber(payload.pending_total, toFiniteNumber(payload.pending_to_pay, toFiniteNumber(payload.pending, 0)));

  const totalSection = normalizeExpenseSummarySection(
    summary.expenses || summary.expense || summary.payment_plan,
    totalFallback
  );
  const paidSection = normalizeExpenseSummarySection(summary.paid, paidFallback);
  const pendingSection = normalizeExpenseSummarySection(summary.pending, pendingFallback);

  return {
    total: totalSection.total,
    paid_total: paidSection.total,
    pending_total: pendingSection.total,
    expenses: totalSection,
    paid: paidSection,
    pending: pendingSection,
  };
};

export const saleDetailExpenseMethods = {
  ensureExpensePageInRange() {
    this.expenseCurrentPage = ensureValidPage(this.expenseCurrentPage, this.expenseTotalPages);
  },
  clearExpenseSearchDebounce() {
    if (!this.expenseSearchDebounceTimer) {
      return;
    }

    clearTimeout(this.expenseSearchDebounceTimer);
    this.expenseSearchDebounceTimer = null;
  },
  scheduleExpenseSearchFetch() {
    this.clearExpenseSearchDebounce();

    this.expenseSearchDebounceTimer = setTimeout(() => {
      this.expenseSearchDebounceTimer = null;
      this.fetchSaleExpenses();
    }, this.expenseSearchDebounceMs);
  },
  normalizeSaleExpensesFilters(filters = {}) {
    const source = (filters && typeof filters === 'object') ? filters : {};

    return {
      search: String(source.search || '').trim(),
      date_start: String(source.date_start || '').trim(),
      date_end: String(source.date_end || '').trim(),
      provider: Array.isArray(source.provider) ? source.provider : [],
      expense_class: Array.isArray(source.expense_class) ? source.expense_class : [],
      category: Array.isArray(source.category) ? source.category : [],
      tags: Array.isArray(source.tags) ? source.tags : [],
      has_xml: Array.isArray(source.has_xml) ? source.has_xml : [],
      currency: Array.isArray(source.currency) ? source.currency : [],
    };
  },
  buildSaleExpensesParams() {
    const filters = this.normalizeSaleExpensesFilters(this.expenseFilters);

    return {
      search: String(this.expenseSearchText || filters.search || '').trim(),
      date_start: filters.date_start,
      date_end: filters.date_end,
      provider: filters.provider,
      expense_class: filters.expense_class,
      category: filters.category,
      tags: filters.tags,
      has_xml: filters.has_xml,
      currency: filters.currency,
      sale_id: this.saleId,
    };
  },
  async ensureSaleExpenseFiltersLoaded() {
    if (this.expenseFiltersLoaded) {
      return;
    }

    try {
      this.expenseFilterOptions = await fetchExpensesFilterOptions();
    } catch (error) {
      this.expenseFilterOptions = getExpensesFilterOptions();
    } finally {
      this.expenseFiltersLoaded = true;
    }
  },
  async fetchSaleExpenses() {
    if (!this.saleId) {
      this.expenseRows = [];
      this.expenseSummary = createInitialExpenseSummary();
      return;
    }

    await this.ensureSaleExpenseFiltersLoaded();

    const params = this.buildSaleExpensesParams();
    const requestKey = JSON.stringify(params);

    if (this.expenseLoading && this.expenseInFlightRequestKey === requestKey) {
      return;
    }

    const requestToken = ++this.expenseListRequestToken;
    this.expenseInFlightRequestKey = requestKey;
    this.expenseLoading = true;

    try {
      const snapshot = await fetchExpensesSnapshot(params);

      if (requestToken === this.expenseListRequestToken) {
        const rows = Array.isArray(snapshot?.rows) ? snapshot.rows : [];
        this.expenseRows = rows;
        this.expenseSummary = normalizeExpenseSummaryPayload(snapshot?.metrics || {});
        this.ensureExpensePageInRange();
      }
    } catch (error) {
      if (requestToken === this.expenseListRequestToken) {
        this.expenseRows = [];
        this.expenseSummary = createInitialExpenseSummary();
      }
    } finally {
      if (requestToken === this.expenseListRequestToken) {
        this.expenseLoading = false;
        if (this.expenseInFlightRequestKey === requestKey) {
          this.expenseInFlightRequestKey = '';
        }
      }
    }
  },
  handleSaleExpensesSearchChange(payload) {
    const nextSearch = String(payload?.search || '').trim();

    this.expenseSearchText = nextSearch;
    this.expenseFilters = {
      ...this.normalizeSaleExpensesFilters(this.expenseFilters),
      search: nextSearch,
    };
    this.expenseCurrentPage = 1;
    this.scheduleExpenseSearchFetch();
  },
  handleSaleExpensesFiltersChange(payload) {
    const nextFilters = this.normalizeSaleExpensesFilters(payload);

    this.expenseFilters = nextFilters;
    this.expenseSearchText = String(nextFilters.search || '').trim();
    this.expenseCurrentPage = 1;
    this.fetchSaleExpenses();
  },
  handleSaleExpensesStatusTabSelect(tabKey) {
    const normalized = String(tabKey || '').trim();
    if (normalized === '' || normalized === this.expenseActiveStatusTab) {
      return;
    }

    this.expenseActiveStatusTab = normalized;
    this.expenseCurrentPage = 1;
  },
  handleSaleExpensesPageChange(nextPage) {
    this.expenseCurrentPage = Number(nextPage || 1);
    this.ensureExpensePageInRange();
  },
  handleSalePayExpense(row) {
    if (this.isPayExpenseSubmitting) {
      return;
    }

    this.payExpenseTarget = row && typeof row === 'object' ? { ...row } : null;
    this.isPayExpenseModalOpen = true;
  },
  handleSaleEditExpense(row) {
    openExpenseEditModalFromRow(this, row, {
      mapInitialData: ({ detail, rowData }) => {
        const mergedPayload = {
          ...rowData,
          ...detail,
        };

        if (String(mergedPayload.status || '').trim() === '') {
          mergedPayload.status = String(rowData.status || '').trim();
        }

        if (Number(mergedPayload.payment_id || 0) <= 0) {
          mergedPayload.payment_id = Number(
            detail.payment_id
            || rowData.payment_id
            || rowData.payment?.id
            || 0
          ) || 0;
        }

        if (mergedPayload.is_approved === undefined || mergedPayload.is_approved === null) {
          mergedPayload.is_approved = rowData.is_approved;
        }

        return mergedPayload;
      },
    });
  },
  handleSaleApproveExpense(row, nextApproved = null) {
    this.approveExpenseTarget = row && typeof row === 'object' ? { ...row } : null;
    this.approveExpenseTargetState = resolveExpenseApprovalTargetState(row, nextApproved);
    this.isApproveExpenseModalOpen = true;
  },
  handleSaleDeleteExpense(row) {
    this.deleteExpenseTarget = row && typeof row === 'object' ? { ...row } : null;
    this.isDeleteExpenseModalOpen = true;
  },
  handleSaleDownloadXml(row) {
    notifyExpenseActionPending(this, row, { actionLabel: EXPENSES_FLOW_TEXT.pending.actions.downloadXml });
  },
  handleSaleDownloadPdf(row) {
    notifyExpenseActionPending(this, row, { actionLabel: EXPENSES_FLOW_TEXT.pending.actions.downloadPdf });
  },
  handleSaleDownloadReceipt(row) {
    notifyExpenseActionPending(this, row, { actionLabel: EXPENSES_FLOW_TEXT.pending.actions.downloadReceipt });
  },
  openExpenseModal(mode = 'create', initialData = null) {
    openExpenseModalState(this, mode, initialData);
  },
  closeSaleApproveExpenseModal() {
    if (this.isApprovingExpense) {
      return;
    }

    this.isApproveExpenseModalOpen = false;
    this.approveExpenseTarget = null;
    this.approveExpenseTargetState = true;
  },
  closeSalePayExpenseModal() {
    if (this.isPayExpenseSubmitting) {
      return;
    }

    this.isPayExpenseModalOpen = false;
    this.isPayExpenseSubmitting = false;
    this.payExpenseTarget = null;
  },
  closeSaleDeleteExpenseModal() {
    if (this.isDeletingExpense) {
      return;
    }

    this.isDeleteExpenseModalOpen = false;
    this.deleteExpenseTarget = null;
  },
  closeExpenseModal() {
    closeExpenseModalState(this);
  },
  async submitSalePayExpense(payload = {}) {
    await submitExpensePayFlow(this, payload, {
      onSuccess: async () => {
        await this.fetchSaleDetail();
      },
    });
  },
  async confirmSaleApproveExpense() {
    await confirmExpenseApproveFlow(this, {
      onSuccess: async () => {
        await this.fetchSaleDetail();
      },
    });
  },
  async confirmSaleDeleteExpense() {
    await confirmExpenseDeleteFlow(this, {
      onSuccess: async () => {
        await this.fetchSaleDetail();
      },
    });
  },
  async submitSaleExpense(payload) {
    await submitExpenseFlow(this, payload, {
      mapRequestData: (requestData) => {
        const isEditMode = String(this.expenseModalMode || '').trim().toLowerCase() === 'edit';
        if (!isEditMode && this.saleId > 0) {
          if (requestData instanceof FormData) {
            requestData.set('sale_id', String(this.saleId));
          } else {
            requestData.sale_id = this.saleId;
          }
        }
      },
      onSuccess: async () => {
        await this.fetchSaleDetail();
      },
    });
  },
  handleSaleClearExpensesFilters() {
    this.isClearSaleExpensesConfirmModalOpen = true;
  },
  closeClearSaleExpensesConfirmModal() {
    if (this.isClearingSaleExpenses) {
      return;
    }

    this.isClearSaleExpensesConfirmModalOpen = false;
  },
  async confirmClearSaleExpenses() {
    if (!this.saleId || this.isClearingSaleExpenses) {
      this.closeClearSaleExpensesConfirmModal();
      return;
    }

    this.isClearingSaleExpenses = true;
    try {
      const response = await expensesService.clearBySale(this.saleId);
      const apiErrorMessage = extractApiErrorMessage(response, 'No fue posible limpiar gastos del negocio.');
      if (apiErrorMessage) {
        this.$toast?.error(apiErrorMessage);
        return;
      }

      this.$toast?.success(extractApiSuccessMessage(response, 'La limpieza de gastos del negocio se completó correctamente.'));
      this.isClearSaleExpensesConfirmModalOpen = false;
      await this.fetchSaleDetail();
    } catch (error) {
      this.$toast?.error(error?.response?.data?.message || error?.message || 'No fue posible limpiar gastos del negocio.');
    } finally {
      this.isClearingSaleExpenses = false;
    }
  },
  handleSaleNewExpenseFromExpenses() {
    this.openExpenseModal();
  },
};
